
import styles from './BundleCell.scss';

import cx from 'classnames';
import React, { useMemo, useRef } from 'react';

import { Solution } from 'src/api/contentful/contentTypes.cloverdotcom';
import { Grid } from 'src/components/Grid/Grid';
import { useDCS } from 'src/hooks/useDCS';
import { FlatEntry } from 'src/services/cms/flattenContentful';
import { BundleSummaryCard } from '../BundleSummaryCard/BundleSummaryCard';

interface BundleCellProps extends React.HTMLAttributes<HTMLDivElement> {
  entry?: FlatEntry<Solution>;
  showAllCards?: boolean;
  isWithSpaceLayout?: boolean;
  showMoreCTA?: string;
  showLessCTA?: string;
}

export const BundleCell = React.forwardRef<HTMLDivElement, BundleCellProps>(({
  entry,
  isWithSpaceLayout,
  className,
  ...props
}) => {
  const { bundles = [], bundleWithTag, tagText } = entry || {};

  const bundleIds = useMemo(() => {
    return bundles.map(bundle => bundle.dcsId);
  }, [bundles]);

  const { bundlesPricings } = useDCS({ bundleIds });

  if (!bundles.length) return null;

  const rootRef = useRef<HTMLDivElement>(null);
  const bundleHeights = useRef<Record<string, Record<string, number>>>({});
  const onResizeHeight = (prop: string, bundleId: string, height: number) => {
    const propHeights = { ...bundleHeights.current[prop], [bundleId]: height };
    bundleHeights.current = { ...bundleHeights.current, [prop]: propHeights };
    const maxHeight = Math.max(...Object.values(propHeights));
    if (rootRef.current) rootRef.current.style.setProperty(prop, `${maxHeight}px`);
  };

  return (
    <Grid container gutter={isWithSpaceLayout} justifyContent="center" ref={rootRef}>
      {bundles.map((item, index) => (
        <Grid key={item.id} item xs={12} md={9} lg className={cx({ [styles.withoutSpaceGrid]: !isWithSpaceLayout, [styles.withSpaceGrid]: isWithSpaceLayout })}>
          <BundleSummaryCard
            bundle={item}
            pricings={bundlesPricings[index]}
            isBestSeller={item?.slug === bundleWithTag?.slug}
            className={styles.card}
            onResizeHeadingHeight={(bundleId, height) => onResizeHeight('--bundle-card-heading-height', bundleId, height)}
            onResizeInnerHeight={(bundleId, height) => onResizeHeight('--bundle-card-inner-height', bundleId, height)}
            onResizeFeaturesHeight={(bundleId, height) => onResizeHeight('--bundle-card-features-height', bundleId, height)}
            {...{ isWithSpaceLayout, tagText }}
            {...props}
          />
        </Grid>
      ))}
    </Grid>
  );
});

BundleCell.displayName = 'BundleCell';
